import React from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import ArrowBack from "@material-ui/icons/ArrowBack";
import AssignmentReturn from "@material-ui/icons/AssignmentReturn";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardIcon from "components/Card/CardIcon.js";
import CardHeader from "components/Card/CardHeader.js";

import styles from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js";

const useStyles = makeStyles(styles);
const useCustom = makeStyles({
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "auto 1fr auto 1fr",
    gap: "8px 24px",
    marginBottom: "20px",
    fontSize: "14px",
  },
  label: { color: "#888", fontWeight: 400, whiteSpace: "nowrap", paddingTop: "2px" },
  value: { fontWeight: 500, color: "#222" },
  statusBadge: {
    display: "inline-block",
    padding: "2px 12px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: 700,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px",
    marginBottom: "20px",
  },
  th: {
    borderBottom: "2px solid #e0e0e0",
    padding: "8px 10px",
    textAlign: "left",
    fontWeight: 600,
    color: "#555",
    whiteSpace: "nowrap",
  },
  thRight: {
    borderBottom: "2px solid #e0e0e0",
    padding: "8px 10px",
    textAlign: "right",
    fontWeight: 600,
    color: "#555",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "8px 10px",
    borderBottom: "1px solid #f0f0f0",
    verticalAlign: "top",
  },
  tdRight: {
    padding: "8px 10px",
    borderBottom: "1px solid #f0f0f0",
    textAlign: "right",
    verticalAlign: "top",
  },
  productNum: { color: "#1565c0", fontWeight: 600 },
  productNote: { fontSize: "11px", color: "#888", marginTop: "2px" },
  summaryTable: {
    width: "100%",
    maxWidth: "360px",
    marginLeft: "auto",
    fontSize: "14px",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "5px 0",
    borderBottom: "1px solid #f5f5f5",
  },
  summaryTotal: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
    fontWeight: 700,
    fontSize: "15px",
    borderTop: "2px solid #e0e0e0",
    marginTop: "4px",
  },
  originalLink: {
    color: "#1565c0",
    fontWeight: 600,
    cursor: "pointer",
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },
});

const GRAPHQL_URL = (process.env.REACT_APP_API_URL || "http://localhost:5001") + "/graphql";

const UPDATE_NOTE_MUTATION = `
  mutation UpdateInvoiceNote($id: String!, $note: String!) {
    updateInvoiceNote(id: $id, note: $note) {
      id note
    }
  }
`;

const INVOICE_QUERY = `
  query GetInvoice($id: String!) {
    getInvoice(id: $id) {
      id invoiceNumber invoiceDate returnNumber status
      creator seller note
      subtotal discount surcharges deliveryFee total paid
      customer { id name customerNumber phone1 }
    }
  }
`;

const DETAILS_QUERY = `
  query GetInvoiceDetails($invoiceId: String!) {
    getInvoiceDetails(invoiceId: $invoiceId) {
      id productNumber productName productNote
      quantity price discountPercent discount salePrice total
    }
  }
`;

const STATUS_COLORS = {
  Processing:          { bg: "#fff3e0", color: "#e65100" },
  Paid:                { bg: "#e8eaf6", color: "#3949ab" },
  Shipped:             { bg: "#e0f7fa", color: "#00838f" },
  Completed:           { bg: "#e8f5e9", color: "#2e7d32" },
  Returned:            { bg: "#fce4ec", color: "#ad1457" },
  Cancelled:           { bg: "#f5f5f5", color: "#757575" },
  "Shipment returned": { bg: "#e3f2fd", color: "#0d47a1" },
};

function formatNum(val) {
  if (val == null) return "0";
  return Number(val).toLocaleString("vi-VN");
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function authHeaders() {
  const token = localStorage.getItem("token");
  return { "Content-Type": "application/json", Authorization: token ? `Bearer ${token}` : "" };
}

export default function ReturnInvoiceDetailPage() {
  const { id } = useParams();
  const history = useHistory();
  const classes = useStyles();
  const custom = useCustom();

  const [invoice, setInvoice] = React.useState(null);
  const [details, setDetails] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [note, setNote] = React.useState("");
  const [noteSaving, setNoteSaving] = React.useState(false);
  const [noteSaved, setNoteSaved] = React.useState(false);

  React.useEffect(() => {
    if (!id) return;
    setLoading(true);

    const gql = (query, variables) =>
      fetch(GRAPHQL_URL, {
        method: "POST",
        headers: authHeaders(),
        credentials: "include",
        body: JSON.stringify({ query, variables }),
      }).then((r) => r.json());

    Promise.all([
      gql(INVOICE_QUERY, { id }),
      gql(DETAILS_QUERY, { invoiceId: id }),
    ])
      .then(([invRes, detRes]) => {
        if (invRes.errors) { setError(invRes.errors[0].message); return; }
        const inv = invRes.data.getInvoice;
        setInvoice(inv);
        setNote(inv.note || "");
        setDetails(detRes.data?.getInvoiceDetails || []);
      })
      .catch(() => setError("Cannot connect to server"))
      .finally(() => setLoading(false));
  }, [id]);

  const saveNote = () => {
    setNoteSaving(true);
    setNoteSaved(false);
    fetch(GRAPHQL_URL, {
      method: "POST",
      headers: authHeaders(),
      credentials: "include",
      body: JSON.stringify({ query: UPDATE_NOTE_MUTATION, variables: { id, note } }),
    })
      .then((r) => r.json())
      .then(({ errors }) => {
        if (errors) { alert(errors[0].message); return; }
        setNoteSaved(true);
        setTimeout(() => setNoteSaved(false), 2000);
      })
      .catch(() => alert("Cannot connect to server"))
      .finally(() => setNoteSaving(false));
  };

  if (loading) return <div style={{ padding: "40px", textAlign: "center" }}>Loading...</div>;
  if (error)   return <div style={{ padding: "40px", color: "#f44336" }}>{error}</div>;
  if (!invoice) return null;

  const sc = STATUS_COLORS[invoice.status] || { bg: "#f5f5f5", color: "#555" };

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="info" icon>
            <CardIcon color="info"><AssignmentReturn /></CardIcon>
            <h4 className={classes.cardIconTitle} style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              {invoice.customer?.name || "No customer"}
              <span style={{ fontSize: "15px", fontWeight: 700, color: "#0d47a1" }}>{invoice.invoiceNumber}</span>
              <span className={custom.statusBadge} style={{ backgroundColor: sc.bg, color: sc.color }}>{invoice.status}</span>
            </h4>
          </CardHeader>
          <CardBody>
            {/* ── Info grid ── */}
            <div className={custom.infoGrid}>
              <span className={custom.label}>Creator</span>
              <span className={custom.value}>{invoice.creator || invoice.seller || "—"}</span>
              <span className={custom.label}>Time</span>
              <span className={custom.value}>{formatDateTime(invoice.invoiceDate)}</span>

              <span className={custom.label}>Original invoice</span>
              <span className={custom.value}>
                {invoice.returnNumber ? (
                  <span
                    className={custom.originalLink}
                    onClick={() => history.push(`/admin/invoices`)}
                  >
                    {invoice.returnNumber}
                  </span>
                ) : "—"}
              </span>
              <span className={custom.label}>Customer</span>
              <span className={custom.value}>
                {invoice.customer
                  ? `${invoice.customer.name}${invoice.customer.phone1 ? " · " + invoice.customer.phone1 : ""}`
                  : "—"}
              </span>

              <span className={custom.label} style={{ paddingTop: "6px" }}>Note</span>
              <span style={{ gridColumn: "2 / 5" }}>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  style={{
                    width: "100%", fontSize: "13px", padding: "6px 8px",
                    border: "1px solid #ddd", borderRadius: "4px", resize: "vertical",
                    fontFamily: "inherit", color: "#333", outline: "none",
                    boxSizing: "border-box",
                  }}
                  placeholder="Add a note..."
                />
                <button
                  onClick={saveNote}
                  disabled={noteSaving}
                  style={{
                    marginTop: "4px", fontSize: "12px", padding: "4px 14px",
                    backgroundColor: noteSaved ? "#4caf50" : "#1565c0",
                    color: "#fff", border: "none", borderRadius: "4px",
                    cursor: noteSaving ? "not-allowed" : "pointer",
                  }}
                >
                  {noteSaving ? "Saving..." : noteSaved ? "Saved!" : "Save note"}
                </button>
              </span>
            </div>

            {/* ── Line items table ── */}
            <table className={custom.table}>
              <thead>
                <tr>
                  <th className={custom.th}>Product number</th>
                  <th className={custom.th}>Product name</th>
                  <th className={custom.thRight}>Quantity</th>
                  <th className={custom.thRight}>Return price</th>
                  <th className={custom.thRight}>Discount</th>
                  <th className={custom.thRight}>Total</th>
                </tr>
              </thead>
              <tbody>
                {details.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: "20px", color: "#aaa", fontSize: "13px" }}>
                      No products recorded
                    </td>
                  </tr>
                ) : (
                  details.map((d) => (
                    <tr key={d.id}>
                      <td className={custom.td}>
                        <span className={custom.productNum}>{d.productNumber || "—"}</span>
                      </td>
                      <td className={custom.td}>
                        <div>{d.productName}</div>
                        {d.productNote && <div className={custom.productNote}>{d.productNote}</div>}
                      </td>
                      <td className={custom.tdRight}>{formatNum(d.quantity)}</td>
                      <td className={custom.tdRight}>{formatNum(d.salePrice)}</td>
                      <td className={custom.tdRight}>{formatNum(d.discount)}</td>
                      <td className={custom.tdRight}>{formatNum(d.total)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* ── Summary ── */}
            <div className={custom.summaryTable}>
              <div className={custom.summaryRow}>
                <span style={{ color: "#555" }}>Sub-total (refund){details.length > 0 ? ` (${details.length})` : ""}</span>
                <span>{formatNum(invoice.subtotal)}</span>
              </div>
              <div className={custom.summaryRow}>
                <span style={{ color: "#555" }}>Discount</span>
                <span>{formatNum(invoice.discount)}</span>
              </div>
              {(invoice.surcharges > 0) && (
                <div className={custom.summaryRow}>
                  <span style={{ color: "#555" }}>Surcharge refund</span>
                  <span>{formatNum(invoice.surcharges)}</span>
                </div>
              )}
              <div className={custom.summaryRow}>
                <span style={{ color: "#555" }}>Return fee</span>
                <span>{formatNum(invoice.deliveryFee)}</span>
              </div>
              <div className={custom.summaryTotal}>
                <span>Total (refund)</span>
                <span style={{ color: "#0d47a1" }}>{formatNum(invoice.total)}</span>
              </div>
              <div className={custom.summaryRow} style={{ paddingTop: "8px" }}>
                <span style={{ color: "#555" }}>Refunded to customer</span>
                <span style={{ fontWeight: 600 }}>{formatNum(invoice.paid)}</span>
              </div>
            </div>

            {/* ── Footer ── */}
            <div style={{ marginTop: "24px" }}>
              <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => history.push("/admin/returns")}>
                Back
              </Button>
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
